import { useState, type ReactNode } from 'react'

import type { ChamberRow, ModuleSnapshot } from '@ctcells/contract'
import { controlOwnerLabel, enumLabel, maskPii, moduleLabel, t, verdictLabel } from '@ctcells/i18n'

import { cx } from '../../atoms/cx.ts'
import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import { SplitBar } from '../../atoms/SplitBar.tsx'
import { StaleMark } from '../../atoms/StaleMark.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { ValueRows, type ValueRow } from '../../atoms/ValueRows.tsx'
import { SidePanel, SidePanelSection } from '../../layout/SidePanel.tsx'
import { dateTimeText, orDash, staleDetail } from '../format.ts'
import type { MetricItem } from '../viewTypes.ts'

import { METRIC_SPECS } from './metricSpecs.ts'
import {
  holdKindOf,
  metrics,
  metricTiles,
  whereOf,
  type ChamberWhere,
  type MonitoringDeviceKey,
} from './monitoringModel.ts'

/**
 * 모니터링 사이드 패널 — 와이어프레임 `compPanel()` 1828행 · README §14-3.
 *
 * 🔴 **제어 버튼이 없다.** 조작은 개발 › 제어에서만 한다(§1-3). maintenance 역할에게도
 *    이 패널에는 조작을 두지 않는다 — eric 이 2026-09-16 에 안내 링크까지 뺐다(1838행).
 * 🔴 **제목이 없다.** 배지와 × 만 있는 머리줄이다. 어떤 컴포넌트인지는 탭이 말한다.
 *
 * 구역 순서 고정: 현재 메트릭 · 진행 · 챔버 표 · 정보.
 */
export interface MonitoringPanelProps {
  deviceKey: MonitoringDeviceKey
  /** 지금 고른 그림 탭 */
  component: string
  /**
   * **여러 구성요소의 메트릭을 한 구역에 모은다** — 콘솔 「장비」 탭(eric 지시 2026-09-21
   * 「메트릭은 오른쪽에 다 같이 정리해서」). 안 넘기면 종전대로 `component` 하나만 본다.
   *
   * 🔴 **새 대응표가 아니다.** 값은 `figureTabs(장비)` 가 낸 탭 키 그대로이고, 메트릭 구성은
   *    여전히 `METRIC_SPECS` 한 곳에서 온다 — 여기서는 «여러 칸을 이어 붙일 뿐»이다.
   * ⚠ 같은 키가 두 번 나오면 앞의 것만 남긴다(염색기의 `slider` 는 셰이커·L 트레이 둘 다에 있다).
   *   **같은 텔레메트리 칸**이라 두 줄이 서면 같은 값이 두 번 적힌다.
   */
  components?: readonly string[] | undefined
  snapshot: ModuleSnapshot | undefined
  chambers: readonly ChamberRow[]
  /**
   * 시료를 **못 봤을 때의 사유**(`GET /samples` 403 · 조회 중). 넘어오면 챔버 표의 빈 문구가
   * 「없음」 대신 이것이 된다 — 🔴 **「못 봤다」와 「없다」는 다르다.** viewer 는 그 경로에
   * 애초에 못 들어가므로(PII 접촉면) 빈 표를 그리면 「이 장비에 시료가 없다」는 거짓이 된다.
   */
  chambersNotice?: string | undefined
  canViewPii: boolean
  /**
   * 닫기. **안 넘기면 닫기 버튼이 없다** — 패널이 항상 열려 있는 자리(패드 · 콘솔 「장비」 탭)다.
   * ⚠ 그림 탭 화면은 계속 넘긴다(탭을 「전체」로 되돌리는 길).
   */
  onClose?: (() => void) | undefined
  /**
   * 「정보」 구역에 덧붙일 행. 모듈 패드가 그 장비의 IP 주소·마지막 진단을 여기 싣는다 —
   * 콘솔은 모듈 4종을 한 화면에서 보므로 넘기지 않는다.
   */
  extraInfo?: readonly ValueRow[]
  /**
   * 구역의 모양. **기본 `rows` = 종전 그대로**(값 행 목록 · 진행 막대). `tiles` 는 장비별 보기 상세 카드
   * (디자인팀 시안 `equipView()` 의 `.side` · eric 2026-10-02 「메트릭 화면도 바꿔주고」)다 —
   * 메트릭 · 정보가 이름 위 · 값 아래 타일이고, 진행 막대는 없다(시안에 없는 구역 · 챔버 표가 같은 사실을 말한다).
   * 🔴 `tiles` 는 **응답에 칸이 없으면 타일을 만들지 않는다**(`metricTiles()`) — 값이 `null` 이면 「미수신」.
   */
  look?: 'rows' | 'tiles'
  /** 머리줄 제목(「원심분리기 상세」). `tiles` 에서만 넘긴다 */
  title?: string | undefined
  /**
   * 제목 바로 아래 · 첫 구역(현재 메트릭) 위에 서는 것 — 장비별 보기 상세 카드의 **비전 칸**(`VisionPane` · eric 2026-10-02
   * 「장비의 상세.. 내용 위에 넣어줘」 · 시안 `.side` 의 `camCell` 자리). `tiles` 에서만 그린다 — 패드(`rows`)는 종전 그대로다.
   */
  lead?: ReactNode
  /**
   * 소모품 칸(`METRIC_SPECS` 의 `source: 'consumables'` — 팁 잔량 · TC 랙)을 메트릭에서 뺀다. 장비별 보기 상세 카드만 켠다 —
   * 그 화면은 오른쪽 열에 소모품 판이 늘 서 있어 같은 수가 두 번 선다(eric 2026-10-02 「장비쪽에.. 소모품은 항상 보이는 거라..
   * 장비 내용 중에 빼줘」). 🔴 패드 · 장비 관점 콘솔은 소모품 판이 없으니 켜지 마라.
   * ⚠ 시약 보관 온도(`reagent_storage` · 냉장 실측)는 소모품이 아니라 장비 상태라 남는다.
   */
  omitConsumables?: boolean | undefined
  /** 「정보」 구역(제어 소유 · 알람 · 응답 확인)을 뺀다 — 6축 상세 전용(eric 2026-10-08 「6축 로봇에서 디테일 부분에서 이거 빼줘」) */
  omitInfo?: boolean | undefined
  /**
   * 「○○의 챔버」(시료 · 단계 표) 구역을 뺀다 — 통합 콘솔 장비 판 전용(eric 2026-10-12 「시료 부분의 염색기의 챔버 이것도 없애줘」).
   * 시료가 어느 장비 · 단계에 있는지는 왼쪽 시료 열 카드가 말한다
   */
  omitChambers?: boolean | undefined
  /**
   * 「현재 메트릭」 구역을 뺀다 — 통합 콘솔 장비 판 전용. 메트릭은 장비 판 그림 영역 오른쪽 위 상자(`EquipmentSheet` `eqsheet__metricbox`)가 말한다
   * (eric 2026-10-12 「왼쪽에 지금 메트릭 내용은 지우고 장비 내부 화면의 오른쪽 위에 사각형 형태로」)
   */
  omitMetrics?: boolean | undefined
}

export function MonitoringPanel({
  deviceKey,
  component,
  components,
  snapshot,
  chambers,
  chambersNotice,
  canViewPii,
  onClose,
  extraInfo = [],
  look = 'rows',
  title,
  lead,
  omitConsumables = false,
  omitInfo = false,
  omitChambers = false,
  omitMetrics = false,
}: MonitoringPanelProps) {
  /* 🔴 안 넘기면 «한 칸짜리 목록»이다 — 종전 동작이 이 한 줄의 특수한 경우가 된다 */
  const shown = components ?? [component]
  const spec = shown
    .flatMap((id) => METRIC_SPECS[id] ?? [])
    .filter((item, index, all) => all.findIndex((other) => other.key === item.key) === index)
    .filter((item) => !omitConsumables || item.source !== 'consumables')
  const items = metrics(snapshot, spec)
  const showChambers = shown.some((id) => id === 'trays' || id === 'tray')
  /*
   * 🔴 **구역 제목만 남기지 않는다.** 소재(`position`)를 모르면 진행 3구간을 못 그리는데
   *    (`ChamberProgress` 머리말 · BR-31) 제목만 서 있으면 「진행이 0 이다」로 읽힌다 —
   *    「탭만 있고 안이 빈 것」과 같은 거짓이다. 그릴 것이 없으면 **구역째 없앤다.**
   */
  const showProgress = showChambers && chambers.some((row) => whereOf(row, deviceKey) !== undefined)
  /*
   * 🔴 **모듈이 스스로 섰으면 그 사실과 «언제부터»를 적는다**(2026-09-22). 카드의 배지는
   *    「섰다 + 종류」까지만 말하고, 경위(`since`)는 자리가 넓은 이 패널이 맡는다.
   * ⚠ 안 섰으면 **줄째 없다** — 「보류 없음」을 적으면 자리만 먹는다.
   */
  const hold = holdKindOf(snapshot)
  const stale = snapshot?.stale === true

  if (look === 'tiles')
    return (
      <SidePanel
        badge={
          stale ? (
            <StaleMark detail={staleDetail(snapshot?.last_seen)} label={t('safety.moduleStale')} />
          ) : null
        }
        title={title}
      >
        {lead}
        <TileSections
          chambers={chambers}
          chambersNotice={chambersNotice}
          canViewPii={canViewPii}
          deviceKey={deviceKey}
          hold={hold}
          showChambers={showChambers && !omitChambers}
          omitMetrics={omitMetrics}
          omitInfo={omitInfo}
          snapshot={snapshot}
          spec={spec}
          stale={stale}
        />
      </SidePanel>
    )

  return (
    <SidePanel
      badge={
        /*
         * 🔴 낡음 표기(정본 07 2026-09-25) — 「응답 없음」 + 마지막 응답 시각. 투명도로 흐리지 않는다.
         *    아래 메트릭은 **마지막 값**이다 — 구역 제목이 그 말을 한다(`panel.metricsStale`).
         */
        stale ? (
          <StaleMark detail={staleDetail(snapshot?.last_seen)} label={t('safety.moduleStale')} />
        ) : null
      }
      {...(onClose === undefined ? {} : { onClose })}
    >
      {items.length === 0 ? null : (
        <SidePanelSection title={stale ? t('panel.metricsStale') : t('panel.metrics')}>
          <ValueRows rows={items.map(toRow)} />
        </SidePanelSection>
      )}

      {showProgress ? (
        <SidePanelSection title={t('panel.progress')}>
          <ChamberProgress chambers={chambers} deviceKey={deviceKey} />
        </SidePanelSection>
      ) : null}

      {showChambers ? (
        <SidePanelSection title={t('panel.chambers')}>
          <ChamberTable canViewPii={canViewPii} chambers={chambers} notice={chambersNotice} />
        </SidePanelSection>
      ) : null}

      <SidePanelSection title={t('panel.info')}>
        <ValueRows
          rows={[
            {
              key: 'owner',
              label: t('panel.controlOwner'),
              value:
                snapshot === undefined ? t('mask.none') : controlOwnerLabel(snapshot.control_owner),
            },
            {
              key: 'lastSeen',
              label: t('column.heartbeat'),
              value: dateTimeText(snapshot?.last_seen),
            },
            {
              key: 'alarms',
              label: t('column.alarm'),
              value:
                snapshot?.active_alarm_count === undefined
                  ? t('mask.none')
                  : String(snapshot.active_alarm_count),
            },
            ...(hold === undefined
              ? []
              : [
                  {
                    key: 'hold',
                    label: t('panel.hold'),
                    value: (
                      <StatusBadge
                        label={t('monitoring.hold', { kind: enumLabel('enum.holdKind', hold) })}
                        originalValue={hold}
                        status="PAUSED"
                      />
                    ),
                  },
                  /* 🔴 **서버가 준 시각 그대로.** 「N분째」 같은 판정을 만들지 않는다 */
                  {
                    key: 'holdSince',
                    label: t('panel.holdSince'),
                    value: dateTimeText(snapshot?.hold?.since),
                  },
                ]),
            ...extraInfo,
          ]}
        />
      </SidePanelSection>
    </SidePanel>
  )
}

/**
 * 상세 카드 «타일» 구역 — 시안 `.sec` · `.tiles` · `.tile`(이름 위 · 값 아래 굵게).
 * 🔴 구역 순서: 현재 메트릭 · {장비}의 챔버 · 정보. 메트릭 타일이 0개면(응답에 칸이 하나도 없으면) 구역째 없다.
 */
function TileSections({
  deviceKey,
  snapshot,
  spec,
  stale,
  hold,
  showChambers,
  chambers,
  chambersNotice,
  canViewPii,
  omitInfo,
  omitMetrics,
}: {
  deviceKey: MonitoringDeviceKey
  snapshot: ModuleSnapshot | undefined
  spec: Parameters<typeof metricTiles>[1]
  stale: boolean
  hold: ReturnType<typeof holdKindOf>
  showChambers: boolean
  chambers: readonly ChamberRow[]
  chambersNotice: string | undefined
  canViewPii: boolean
  omitInfo: boolean
  omitMetrics: boolean
}) {
  const tiles = metricTiles(snapshot, spec)
  /*
   * 정보 타일 — 🔴 같은 원칙이다. 스냅샷이 없으면(모듈 미등록) 제어 소유도 모른다 → 타일 없음.
   * 응답 확인(`last_seen`)은 계약상 늘 있는 칸(nullable)이라 `null` 이면 「미수신」, 활성 알람 수는 옵션 칸이라 없으면 타일 없음.
   */
  const info: { key: string; label: string; value: ReactNode | null }[] =
    snapshot === undefined
      ? []
      : [
          {
            key: 'owner',
            label: t('panel.controlOwner'),
            value: controlOwnerLabel(snapshot.control_owner),
          },
          ...(snapshot.active_alarm_count === undefined
            ? []
            : [
                {
                  key: 'alarms',
                  label: t('column.alarm'),
                  value: t('panel.alarmCount', { count: snapshot.active_alarm_count }),
                },
              ]),
          {
            key: 'lastSeen',
            label: t('column.heartbeat'),
            value: snapshot.last_seen == null ? null : dateTimeText(snapshot.last_seen),
          },
          ...(hold === undefined
            ? []
            : [
                {
                  key: 'hold',
                  label: t('panel.hold'),
                  value: (
                    <StatusBadge
                      label={t('monitoring.hold', { kind: enumLabel('enum.holdKind', hold) })}
                      originalValue={hold}
                      status="PAUSED"
                    />
                  ),
                },
                {
                  key: 'holdSince',
                  label: t('panel.holdSince'),
                  value: snapshot.hold?.since == null ? null : dateTimeText(snapshot.hold.since),
                },
              ]),
        ]
  return (
    <>
      {tiles.length === 0 || omitMetrics ? null : (
        <SidePanelSection title={stale ? t('panel.metricsStale') : t('panel.metrics')}>
          <Tiles items={tiles} />
        </SidePanelSection>
      )}
      {showChambers ? (
        <SidePanelSection title={t('panel.chambersOf', { name: moduleLabel(deviceKey) })}>
          <ChamberTable canViewPii={canViewPii} chambers={chambers} notice={chambersNotice} />
        </SidePanelSection>
      ) : null}
      {info.length === 0 || omitInfo ? null : (
        <SidePanelSection title={t('panel.info')}>
          <Tiles items={info} wide />
        </SidePanelSection>
      )}
    </>
  )
}

/** 타일 격자. 🔴 값이 `null` 이면 「미수신」 — 화면에는 흐린 대시(—) · 보조기술 · 툴팁은 「미수신」(eric 2026-10-19) */
function Tiles({
  items,
  wide = false,
}: {
  items: readonly { key: string; label: string; value: ReactNode | null }[]
  wide?: boolean
}) {
  return (
    <dl className={cx('eqtiles', wide && 'eqtiles--3')}>
      {items.map((item) => (
        <div className="eqtile" data-metric={item.key} key={item.key}>
          <dt className="eqtile__label">{item.label}</dt>
          <dd className="eqtile__value">
            {item.value === null ? (
              <span className="eqtile__nv" title={t('panel.unreceived')}>
                {/* 화면에는 대시 하나(eric 2026-10-19 「미수신이면.. - 이렇게.. 대시로」) · 보조기술은 「미수신」을 읽는다 */}
                <span aria-hidden="true">—</span>
                <span className="a-sr">{t('panel.unreceived')}</span>
              </span>
            ) : (
              item.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}

function toRow(item: MetricItem) {
  return { key: item.key, label: item.label, value: item.value }
}

/**
 * 「진행」 — 남은 시료 · 작업 중 · 완료를 개수로 가른다(`progSplit()` 1009행).
 *
 * 🔴 **소재(`position`)를 모르면 이 구역이 통째로 없다**(2026-09-20). 세 칸이 전부 소재 판정 위에
 *    서는데 live 에는 그 축이 없다(`ChamberRow` 머리말 · BR-31). 판정 못 한 행을 「남은 시료」로
 *    접으면 **아무것도 모르는 화면이 「전부 작업 전」이라고 단정**한다.
 * ⚠ 구역을 세울지는 호출부(`showProgress`)가 정한다 — 제목만 남으면 「진행이 0」으로 읽힌다.
 */
function ChamberProgress({
  chambers,
  deviceKey,
}: {
  chambers: readonly ChamberRow[]
  deviceKey: MonitoringDeviceKey
}) {
  const where = chambers.map((row) => whereOf(row, deviceKey))
  if (where.every((value) => value === undefined)) return null

  const count = (value: ChamberWhere) => where.filter((item) => item === value).length

  return (
    <SplitBar
      label={t('panel.progress')}
      segments={[
        { key: 'pre', label: t('panel.remainingSamples'), value: count('pre') },
        { key: 'now', label: t('panel.inProgress'), value: count('inModule'), tone: 'current' },
        { key: 'done', label: t('panel.done'), value: count('post'), tone: 'done' },
      ]}
    />
  )
}

/**
 * 챔버 표 — **시료 ID 기준 정렬**이다(§14-4). 트레이 물리 배치 순서가 아니다.
 * 🔴 행 클릭은 **그 자리에서 아래로 펼친다.** 팝업을 띄우지 않는다(2026-09-16 지시 · 999행).
 */
function ChamberTable({
  chambers,
  canViewPii,
  notice,
}: {
  chambers: readonly ChamberRow[]
  canViewPii: boolean
  /** 못 본 사유. 있으면 빈 표의 문구가 이것이다 — 「없음」이라고 단언하지 않는다 */
  notice?: string | undefined
}) {
  const [openKey, setOpenKey] = useState<string | null>(null)

  /*
   * 🔴 **열은 「그 값이 실제로 오는가」로 정한다**(2026-09-20). `slot`·`position` 은 서버가 한 번도
   *    주지 않으므로(`ChamberRow` 머리말 · BR-31) live 에서는 **열 자체를 만들지 않는다** —
   *    `—` 로 채운 열을 깔아 두지 않는다(CLAUDE.md). demo 시나리오는 그 값을 채우므로 그대로 보인다.
   * ⚠ 판정 축은 **데이터**다(`row.slot !== undefined`). `dataSourceFor()` 로 가르지 않는다 —
   *   이 컴포넌트는 콘솔·패드가 같이 쓰고 어느 쪽이 무엇을 넘겼는지 모르는 편이 맞다.
   */
  const hasSlot = chambers.some((row) => row.slot !== undefined)
  const hasPosition = chambers.some((row) => row.position !== undefined)

  const columns: Column<ChamberRow>[] = [
    {
      key: 'sample',
      header: t('column.sample'),
      nowrap: true,
      cell: (row) => <span className="mono">{maskPii(row.sample_id, canViewPii)}</span>,
    },
    ...(hasSlot
      ? [
          {
            key: 'slot',
            header: t('column.cell'),
            nowrap: true,
            cell: (row: ChamberRow) => <span className="mono">{orDash(row.slot)}</span>,
          },
        ]
      : []),
    ...(hasPosition
      ? [
          {
            key: 'position',
            header: t('column.location'),
            cell: (row: ChamberRow) => orDash(row.position),
          },
        ]
      : []),
    // 단계 표기는 다른 화면과 «같은 사전»을 쓴다(`SamplesScreen`·`BatchDetailScreen` 과 한 축)
    {
      key: 'stage',
      header: t('column.step'),
      cell: (row: ChamberRow) => enumLabel('enum.sampleStage', row.stage),
    },
  ]

  return (
    <DataTable
      caption={t('panel.chambers')}
      columns={columns}
      density="compact"
      emptyLabel={notice ?? t('state.empty.generic')}
      expandedKey={openKey}
      onToggleExpand={(key) => setOpenKey((current) => (current === key ? null : key))}
      /*
       * 펼친 줄도 **있는 값만** 싣는다. `slot`·`position`·`verdict` 는 서버가 주지 않으므로
       * live 에서는 행이 서지 않는다 — 「종류」(대조 여부)와 「단계」는 실물이라 항상 있다.
       */
      renderExpanded={(row) => (
        <div className="expand">
          <ValueRows
            rows={[
              ...(row.slot === undefined
                ? []
                : [{ key: 'slot', label: t('column.cell'), value: row.slot }]),
              ...(row.position === undefined
                ? []
                : [{ key: 'position', label: t('column.location'), value: row.position }]),
              ...(row.verdict === undefined || row.verdict === null
                ? []
                : [
                    {
                      key: 'verdict',
                      label: t('column.verdict'),
                      value: <StatusBadge label={verdictLabel(row.verdict)} status={row.verdict} />,
                    },
                  ]),
              {
                key: 'reference',
                label: t('column.kind'),
                value: row.reference ? t('monitoring.reference') : t('mask.none'),
              },
              {
                key: 'module',
                label: t('column.module'),
                value: orDash(row.current_module),
              },
              {
                key: 'stage',
                label: t('column.step'),
                value: enumLabel('enum.sampleStage', row.stage),
              },
            ]}
          />
        </div>
      )}
      rowKey={(row) => row.sample_id}
      rows={[...chambers].sort((a, b) => {
        if (a.reference !== b.reference) return a.reference ? 1 : -1
        return a.sample_id.localeCompare(b.sample_id, 'ko')
      })}
    />
  )
}
